import type { ProjectTask, TaskGroup } from '../types';
import { buildChildTasksByParent } from './subtaskMigration';
import { taskMatchesStructureFilterIds } from './subtaskStructures';
import type { GroupTimelineSection } from './groupTimeline';

export type StructureFilterValue = string | '__unassigned';

export type GanttFlatRow =
  | { type: 'section'; section: GroupTimelineSection; taskCount: number }
  | { type: 'group'; group: TaskGroup; taskCount: number }
  | { type: 'task'; task: ProjectTask; childCount: number }
  | { type: 'subtask'; task: ProjectTask; parentId: string };

export function isGanttTaskFlatRow(
  row: GanttFlatRow,
): row is Extract<GanttFlatRow, { type: 'task' | 'subtask' }> {
  return row.type === 'task' || row.type === 'subtask';
}

export function taskMatchesStructureFilter(
  task: ProjectTask,
  filter: Set<StructureFilterValue> | null,
): boolean {
  if (!filter || filter.size === 0) return true;
  return taskMatchesStructureFilterIds(task, filter);
}

function parentVisibleWithFilter(
  parent: ProjectTask,
  children: ProjectTask[],
  structureFilter: Set<StructureFilterValue> | null,
): boolean {
  if (!structureFilter || structureFilter.size === 0) return true;
  if (taskMatchesStructureFilter(parent, structureFilter)) return true;
  return children.some((c) => taskMatchesStructureFilter(c, structureFilter));
}

function visibleChildren(
  children: ProjectTask[],
  structureFilter: Set<StructureFilterValue> | null,
  parent: ProjectTask,
): ProjectTask[] {
  if (!structureFilter || structureFilter.size === 0) return children;
  if (taskMatchesStructureFilter(parent, structureFilter)) return children;
  return children.filter((c) => taskMatchesStructureFilter(c, structureFilter));
}

export function buildGanttFlatRows(params: {
  groups: TaskGroup[];
  tasks: ProjectTask[];
  collapsedGroups: Set<string>;
  expandedParentIds: Set<string>;
  structureFilter: Set<StructureFilterValue> | null;
  sections?: GroupTimelineSection[];
  collapsedSections?: Set<string>;
}): GanttFlatRow[] {
  const {
    groups,
    tasks,
    collapsedGroups,
    expandedParentIds,
    structureFilter,
    sections,
    collapsedSections,
  } = params;
  const childByParent = buildChildTasksByParent(tasks);
  const topLevel = tasks.filter((t) => !t.parentTaskId);

  const tasksByGroup = new Map<string, ProjectTask[]>();
  groups.forEach((g) => tasksByGroup.set(g.id!, []));
  const ungrouped: ProjectTask[] = [];
  for (const t of topLevel) {
    const gid = t.groupId || '';
    if (tasksByGroup.has(gid)) tasksByGroup.get(gid)!.push(t);
    else ungrouped.push(t);
  }
  tasksByGroup.forEach((arr) => arr.sort((a, b) => (a.position || 0) - (b.position || 0)));
  ungrouped.sort((a, b) => (a.position || 0) - (b.position || 0));

  const rows: GanttFlatRow[] = [];
  const sortedGroups = [...groups].sort((a, b) => (a.position || 0) - (b.position || 0));

  const appendTaskWithChildren = (task: ProjectTask) => {
    const children = childByParent.get(task.id!) ?? [];
    if (!parentVisibleWithFilter(task, children, structureFilter)) return;
    const visibleKids = visibleChildren(children, structureFilter, task);
    rows.push({ type: 'task', task, childCount: children.length });
    if (task.id && expandedParentIds.has(task.id)) {
      for (const child of visibleKids) {
        rows.push({ type: 'subtask', task: child, parentId: task.id! });
      }
    }
  };

  const emitGroup = (g: TaskGroup) => {
    const gTasks = (tasksByGroup.get(g.id!) ?? []).filter((t) =>
      parentVisibleWithFilter(t, childByParent.get(t.id!) ?? [], structureFilter),
    );
    rows.push({ type: 'group', group: g, taskCount: gTasks.length });
    if (!collapsedGroups.has(g.id!)) {
      for (const t of tasksByGroup.get(g.id!) ?? []) {
        appendTaskWithChildren(t);
      }
    }
  };

  if (sections?.length) {
    const seen = new Set<string>();
    const groupsById = new Map(sortedGroups.filter((g) => g.id).map((g) => [g.id!, g]));
    for (const section of sections) {
      const sectionGroups = section.groupIds
        .map((id) => groupsById.get(id))
        .filter((g): g is TaskGroup => !!g);
      const sectionTaskCount = sectionGroups.reduce((sum, g) => {
        const gTasks = (tasksByGroup.get(g.id!) ?? []).filter((t) =>
          parentVisibleWithFilter(t, childByParent.get(t.id!) ?? [], structureFilter),
        );
        return sum + gTasks.length;
      }, 0);
      rows.push({ type: 'section', section, taskCount: sectionTaskCount });
      const collapsed = !!collapsedSections?.has(section.id);
      for (const g of sectionGroups) {
        if (g.id) seen.add(g.id);
        if (!collapsed) emitGroup(g);
      }
    }
    for (const g of sortedGroups) {
      if (g.id && !seen.has(g.id)) emitGroup(g);
    }
  } else {
    for (const g of sortedGroups) {
      emitGroup(g);
    }
  }
  for (const t of ungrouped) {
    appendTaskWithChildren(t);
  }
  return rows;
}

export function buildTaskRowIndexFromFlatRows(flatRows: GanttFlatRow[]): Map<string, number> {
  const map = new Map<string, number>();
  let row = 0;
  for (const r of flatRows) {
    if (r.type === 'group' || r.type === 'section') {
      row += 1;
      continue;
    }
    if (r.task.id) map.set(r.task.id, row);
    row += 1;
  }
  return map;
}
